import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store';
import { getAuthRoleName, selectCurrentUser, type AuthRole } from '../redux/slices/auth.slice';
import { fetchPendingWork } from '../services/pendingWork.service';
import { usePermission } from '../hooks/usePermission';
import { HOME_PERMISSIONS } from '../constants/permissions.constants';
import { EMPTY_PENDING_WORK, type PendingWorkResponse } from '../types/pendingWork.types';

type PendingWorkContextValue = {
  pendingWork: PendingWorkResponse;
  loading: boolean;
  error: string | null;
  /** Refresh counts; scope defaults to user primary / global. */
  refreshPendingWork: (scope?: string) => Promise<void>;
  /** Default scope when not on Inicio with a selected tab. */
  defaultScope: string;
};

const PendingWorkContext = createContext<PendingWorkContextValue | null>(null);

export function usePendingWork(): PendingWorkContextValue {
  const ctx = useContext(PendingWorkContext);
  if (!ctx) {
    throw new Error('usePendingWork must be used within PendingWorkProvider');
  }
  return ctx;
}

/** Optional hook when provider may be absent (e.g. public routes). */
export function usePendingWorkOptional(): PendingWorkContextValue | null {
  return useContext(PendingWorkContext);
}

function isPlatformAdminRole(roles?: AuthRole[]): boolean {
  return roles?.some((r) => getAuthRoleName(r)?.trim().toLowerCase() === 'admin') ?? false;
}

function resolveDefaultScope(user: ReturnType<typeof selectCurrentUser>): string {
  if (!user) return 'global';
  const canGlobal = isPlatformAdminRole(user.roles) || user.canSellFromOtherLocations === true;
  if (canGlobal) return 'global';
  if (user.primaryLocationId != null) return String(user.primaryLocationId);
  return 'global';
}

export const PendingWorkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useSelector(selectCurrentUser);
  const token = useSelector((state: RootState) => state.auth.token);
  const defaultScope = useMemo(() => resolveDefaultScope(user), [user]);
  const canViewPendingWork = usePermission(HOME_PERMISSIONS.PENDING_WORK_VIEW);

  const [pendingWork, setPendingWork] = useState<PendingWorkResponse>(EMPTY_PENDING_WORK);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const refreshPendingWork = useCallback(
    async (scope?: string) => {
      // Without the pending-work permission the endpoint returns 403; keep the empty state.
      if (!canViewPendingWork) return;
      const effectiveScope = scope ?? defaultScope;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const requestId = ++requestIdRef.current;

      setLoading(true);
      setError(null);
      try {
        const data = await fetchPendingWork(effectiveScope, controller.signal);
        if (requestId !== requestIdRef.current) return;
        setPendingWork(data);
      } catch (err) {
        if (controller.signal.aborted || requestId !== requestIdRef.current) return;
        console.error('Error fetching pending-work:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar pendientes');
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [defaultScope, canViewPendingWork],
  );

  useEffect(() => {
    if (!token || !canViewPendingWork) {
      abortRef.current?.abort();
      setPendingWork(EMPTY_PENDING_WORK);
      setError(null);
      setLoading(false);
      return;
    }
    void refreshPendingWork(defaultScope);
  }, [token, defaultScope, refreshPendingWork, canViewPendingWork]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const value = useMemo(
    () => ({
      pendingWork,
      loading,
      error,
      refreshPendingWork,
      defaultScope,
    }),
    [pendingWork, loading, error, refreshPendingWork, defaultScope],
  );

  return <PendingWorkContext.Provider value={value}>{children}</PendingWorkContext.Provider>;
};
